import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  QueryReturnValue
} from '@reduxjs/toolkit/query/react';

import { RootState } from '../store';
import { updateHbTime, updateSessionStatus } from '../store/authSlice';
import { getDateDiffMinutes } from '../utils';
import { runGlobalLogin } from './globalApi';

export const preUrl = '/api/v1/';
const HB_TRIGGER_MINUTES = 9;

export interface IReqProps {
  region?: string;
  regionObj?: Record<string, any>;
  apiNames?: string[];
  accountIds?: string[];
  instanceId?: string;
  tzCode?: string;
  groupId?: string;
  taskId?: string;
  etaMins?: number;
  time?: Date;
  page?: number;
  pageSize?: number;
  searchObj?: Record<string, string>;
  sessionId?: number | string;
  action?: string | number;
  sortColumn?: string;
  sortType?: string;
  to_version?: string;
  imageVersion?: string;
  adomVersion?: string;
  snapShotId?: string;
  snapShotName?: string;
  snapShotDesc?: string;
  platform?: string;
  firmwareVersion?: string;
}

export interface QueryData {
  group_id: string | number;
  account_id: string | number;
  user_id: string | number;
  org_id: string | number;
  account_node_id: string | number;
  access_from_user_id: string | number;
  access_from_account_id: string | number;
  instance_id: string | number;
  instid: string | number;
  ha_id: string | number;
}

export interface RegionQueryArgs extends FetchArgs {
  regionUrl?: string;
  token?: string;
}

const getAuthHeaders = (token?: string) => ({
  Authorization: `token ${token || ''}`
});

const getRegionName = (region?: Record<string, any>) =>
  region?.region || region?.alias || '';

const buildApiUrl = (regionUrl = '', endpoint = ''): string =>
  `${regionUrl}${preUrl}${endpoint}`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  mode: 'cors',
  credentials: 'include',
  timeout: 60000,
  prepareHeaders: (headers) => {
    headers.set('Content-type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  }
});

const baseQuery = async (
  args: string | RegionQueryArgs,
  api: any,
  extraOptions: any
) => {
  let modifiedArgs: FetchArgs;

  if (typeof args === 'string') {
    modifiedArgs = {
      url: args
    };
  } else {
    const { regionUrl, token, url, headers, ...rest } = args;

    modifiedArgs = {
      ...rest,
      url: buildApiUrl(regionUrl, url),
      headers: {
        ...headers,
        ...(token ? getAuthHeaders(token) : {})
      }
    };
  }

  return rawBaseQuery(modifiedArgs, api, extraOptions);
};

const triggerHeartbeat = async (api: any, extraOptions: any): Promise<void> => {
  const state = api.getState() as RootState;

  const regions = state.auth.needLoginRegions;
  const hbTime = state.auth.hbTime;

  const shouldTrigger =
    typeof regions === 'object' &&
    Object.keys(regions).length > 0 &&
    getDateDiffMinutes(new Date(), hbTime) > HB_TRIGGER_MINUTES;

  if (!shouldTrigger) return;

  await Promise.all(
    Object.keys(regions).map((name) =>
      baseQuery(
        {
          regionUrl: regions[name]?.url,
          token: regions[name]?.token,
          url: 'system/hb',
          method: 'GET'
        },
        api,
        extraOptions
      )
    )
  );

  api.dispatch(updateHbTime(new Date()));
};

const handleUnauthorizedError = async (
  error: Record<string, any>,
  args: any,
  api: any,
  extraOptions: any
) => {
  const isUnauthorized =
    error?.status === 401 ||
    error?.originalStatus === 401 ||
    error?.data?.error_code === -30001;

  const isLoginRequest = args?.url?.includes('user/login');

  // if (!isUnauthorized || isLoginRequest) {
  //   return;
  // }
  if (!isUnauthorized) {
    return;
  }

  // global token expired or invalid
  if (isUnauthorized && isLoginRequest) {
    console.warn('Global token expired or invalid. Retrying login.');
    const loginResult = await runGlobalLogin(api, baseQuery);

    if (!loginResult?.glob_token) {
      api.dispatch(updateSessionStatus('REVOKED'));
      return;
    }

    // retry original request
    await baseQuery(args, api, extraOptions);
    return;
  }

  console.warn('401 error:', error?.data);

  api.dispatch(
    updateSessionStatus(
      error?.data?.detail === 'TOKEN_EXPIRED' ? 'EXPIRED' : 'REVOKED'
    )
  );
};

const normalizeServerError = (error: Record<string, any>) => {
  if (error?.originalStatus !== 500) {
    return null;
  }

  return {
    error: {
      status: error.originalStatus,
      data: {
        message: error.error
      }
    }
  };
};

// const testGlobalLogin = async (args: any, api: any, extraOptions: any) => {
//   console.warn('Testing global login...');
//   const isLoginRequest = args?.url?.includes('user/login');
//   if (!isLoginRequest) {
//     return;
//   }

//   const result = await runGlobalLogin(api, baseQuery);

//   await baseQuery(args, api, extraOptions);
// };

const baseQueryCatchError: BaseQueryFn<
  string | RegionQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  const error = result.error as Record<string, any>;

  // testGlobalLogin(args, api, extraOptions);

  if (error) {
    console.warn('query error:', error);

    const normalizedError = normalizeServerError(error);

    if (normalizedError) {
      return normalizedError;
    }

    await handleUnauthorizedError(error, args, api, extraOptions);
  } else {
    await triggerHeartbeat(api, extraOptions);
  }

  return result;
};

export const regionRequest = (
  region: Record<string, any>,
  url: string,
  options: Partial<RegionQueryArgs> = {}
): RegionQueryArgs => ({
  url,
  regionUrl: region?.url,
  token: region?.token,
  ...options
});

const loginRegion = async (
  arg: Record<string, any>,
  fetchWithBQ: any
): Promise<QueryReturnValue<unknown, FetchBaseQueryError, {}>> => {
  const loginRequest = () =>
    fetchWithBQ(
      regionRequest(
        {
          url: arg?.region?.url,
          token: arg?.region?.globalToken
        },
        'user/login',
        {
          method: 'POST',
          body: {
            token: arg?.region?.globalToken
          }
        }
      )
    );

  let response = await loginRequest();

  if (response.error) {
    const { data } = response.error as any;

    const shouldRetry =
      data?.error_code === -30000 &&
      data?.error_desc === 'The request payload is invalid.';

    if (shouldRetry) {
      response = await loginRequest();
    }
  }

  return response;
};

// Tracks the latest in-flight region login per region, so that when the
// network is slow and a newer login starts before an older one finishes,
// only the newest response is considered valid (old responses are ignored).
const regionLoginSeq: Record<string, number> = {};
const getRegionSeqKey = (prefix: string, arg: Record<string, any>) =>
  `${prefix}|${arg?.region?.url || arg?.region?.alias || 'unknown'}`;

export const regionApi = createApi({
  reducerPath: 'regionApi',

  baseQuery: baseQueryCatchError,

  tagTypes: [
    'Instance',
    'InstanceUrl',
    'Provision',
    'Snapshot',
    'Asset',
    'Helper',
    'License',
    'HelperInfo',
    'GroupInstance',
    'GroupAccount',
    'RegionInstance',
    'HA',
    'VmStatus',
    'UpgradeRequest'
  ],

  endpoints: (build) => ({
    regionLogin: build.query<any, Record<string, any>>({
      // Normalize the cache key so concurrent logins for the same region +
      // global token share a single RTK Query entry (no duplicate requests
      // even when different call sites pass slightly different args).
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs?.region?.url || ''}|${queryArgs?.region?.globalToken || ''}|${
          queryArgs?.enableTimezone ? 'tz' : 'no-tz'
        }`,
      async queryFn(arg, api, _extraOptions, fetchWithBQ) {
        const seqKey = getRegionSeqKey('login', arg);
        const mySeq = (regionLoginSeq[seqKey] =
          (regionLoginSeq[seqKey] || 0) + 1);

        const loginResponse = await loginRegion(arg, fetchWithBQ);

        if (loginResponse.error) {
          throw loginResponse.error;
        }

        const region = loginResponse.data as Record<string, any>;

        const regionName = getRegionName(arg?.region);

        const noticePromise = fetchWithBQ(
          regionRequest(
            {
              url: arg?.region?.url,
              token: region?.token
            },
            `notification?region=${regionName}`,
            {
              method: 'GET'
            }
          )
        );

        const timezonePromise = arg?.enableTimezone
          ? fetchWithBQ(
              regionRequest(
                {
                  url: arg?.region?.url,
                  token: region?.token
                },
                `timezone?region=${regionName}`,
                {
                  method: 'GET'
                }
              )
            )
          : Promise.resolve({
              data: []
            });

        const defaultTimezonePromise = arg?.enableTimezone
          ? fetchWithBQ(
              regionRequest(
                {
                  url: arg?.region?.url,
                  token: region?.token
                },
                `system/get_default_timezone?region=${regionName}`,
                {
                  method: 'GET'
                }
              )
            )
          : Promise.resolve({
              data: {
                default_timezone_code: ''
              }
            });

        const [
          { data: notice },
          { data: timezone },
          { data: defaultTimezone }
        ] = await Promise.all([
          noticePromise,
          timezonePromise,
          defaultTimezonePromise
        ]);

        // Only the newest login is valid. If a newer region login for the
        // same region was started while this (slow) request was in flight,
        // never let this older response overwrite a fresher token.
        if (regionLoginSeq[seqKey] !== mySeq) {
          const storedToken =
            (api.getState() as RootState).auth?.regions?.[
              arg?.region?.alias || arg?.region?.region
            ]?.token || '';
          region.token = storedToken || region?.token;
        }

        return {
          data: {
            ...region,
            notice,
            timezone,
            defaultTimezone:
              (defaultTimezone as any)?.default_timezone_code || ''
          }
        };
      }
    }),

    singleRegionLogin: build.query<any, Record<string, any>>({
      // Same stable cache key so concurrent identical logins are coalesced.
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs?.region?.url || ''}|${queryArgs?.region?.globalToken || ''}`,
      async queryFn(arg, api, _extraOptions, fetchWithBQ) {
        const seqKey = getRegionSeqKey('single', arg);
        const mySeq = (regionLoginSeq[seqKey] =
          (regionLoginSeq[seqKey] || 0) + 1);
        const loginResponse = await loginRegion(arg, fetchWithBQ);
        if (loginResponse.error) {
          throw loginResponse.error;
        }
        const region = (loginResponse.data || {}) as Record<string, any>;
        if (regionLoginSeq[seqKey] !== mySeq) {
          const storedToken =
            (api.getState() as RootState).auth?.regions?.[
              arg?.region?.alias || arg?.region?.region
            ]?.token || '';
          region.token = storedToken || region?.token;
        }
        return { data: region };
      }
    }),

    groupAccountsByPage: build.query<Record<string, any>, IReqProps>({
      query: (arg) => {
        const {
          page = 1,
          pageSize = 50,
          regionObj = {},
          groupId,
          searchObj,
          sortColumn,
          sortType = 'asc'
        } = arg;

        const params: Record<string, any> = {
          group_id: groupId,
          page,
          page_size: pageSize
        };

        if (searchObj?.key && searchObj?.value) {
          params[searchObj.key] = searchObj.value;
        }

        if (sortColumn) {
          params.order_by = sortColumn;
          params.order = sortType;
        }

        return regionRequest(regionObj, 'group/get_account_list', {
          method: 'GET',
          params
        });
      },

      transformResponse: (result: Record<string, any>, _meta, arg) => ({
        group_id: arg?.groupId,
        page: arg?.page,
        pageSize: arg?.pageSize,
        data: {
          ...result,
          results: result.results.map(
            (item: Record<string, any>, index: number) => ({
              ...item,
              order: index
            })
          )
        }
      })
    })
  })
});

export const getQueryParameters = (
  obj: Record<string, any>,
  extraOpts?: Record<string, any>
): QueryData => ({
  group_id: obj?.group_id,
  account_id: obj?.account_id,
  user_id: obj?.user_id,
  org_id: obj?.org_id,
  account_node_id: obj?.account_node_id,
  access_from_user_id: obj?.access_from_user_id,
  access_from_account_id: obj?.access_from_account_id,
  instance_id: obj?.instance_id,
  instid: obj?.instid,
  ha_id: obj?.ha_id,
  ...(extraOpts || {})
});

export const {
  usePrefetch,
  useRegionLoginQuery,
  useSingleRegionLoginQuery,
  useLazyRegionLoginQuery,
  useGroupAccountsByPageQuery,
  useLazyGroupAccountsByPageQuery
} = regionApi;
